// The admin session's token handling: the only admin code the public site's
// entry needs (AuthContext ends an impersonation on sign-out, #623). The rest
// of the admin transport lives in admin-v2/data/adminClient.ts, so it stays
// out of the fan bundle (#1294).

import type { LoginResponse } from './api/types'

export const ADMIN_API_BASE_URL: string = import.meta.env.VITE_API_URL || '/api/v1'
const TOKEN_KEY = 'mcl_admin_token'
const USER_KEY = 'mcl_admin_user'

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

/** `Authorization: Bearer <token>` when signed in, else no headers. */
export function adminAuthHeaders(): Record<string, string> {
  const token = getAdminToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * The stored token is present and not expired. exp is compared against
 * server-corrected time so a skewed device clock can't declare a valid token
 * expired (the offset, device − server in ms, is captured at login by
 * AuthContext).
 */
export function hasValidAdminToken(): boolean {
  const token = getAdminToken()
  if (!token) return false
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    const decoded = JSON.parse(json)
    const offset = Number(localStorage.getItem('mcl_clock_offset_ms')) || 0
    return decoded.exp * 1000 > Date.now() - offset - 60_000
  } catch {
    return false
  }
}

/** Forget the stored session (token and user). */
export function clearAdminSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

/** Email + password sign-in. */
export async function adminLogin(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${ADMIN_API_BASE_URL}/admin/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error', message: response.statusText }))
    throw new Error(errorData.message || `Login failed: ${response.statusText}`)
  }
  return response.json()
}

/** Ask for a password-reset email (#1381). Same answer whether or not the email has an account. */
export async function requestAdminPasswordReset(email: string, locale?: string): Promise<void> {
  const response = await fetch(`${ADMIN_API_BASE_URL}/admin/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, ...(locale ? { locale } : {}) }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw Object.assign(new Error(errorData.message || response.statusText), { status: response.status })
  }
}

/** Spend a reset link's token on a new password (#1381). Rejects with `reason: 'invalid_token'` for a dead link. */
export async function resetAdminPassword(token: string, password: string): Promise<void> {
  const response = await fetch(`${ADMIN_API_BASE_URL}/admin/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw Object.assign(new Error(errorData.message || response.statusText), {
      status: response.status,
      reason: errorData.reason as string | undefined,
      field: errorData.field as string | undefined,
    })
  }
}

/**
 * End a super-admin impersonation session server-side (#623), so its token
 * stops working now rather than when it expires. Best effort: never throws
 * (signing out locally must not wait on or fail with the network).
 */
export async function endImpersonation(token: string): Promise<void> {
  try {
    await fetch(`${ADMIN_API_BASE_URL}/admin/auth/impersonation/end`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    })
  } catch {
    // Offline or already gone: the token still expires on its own.
  }
}
